import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useMessage } from '@tmapy/intl';
import {
  BtnBar,
  CustomSelect,
  DangerAlert,
  FormItem,
  FormRow,
  SecondaryBtn,
  SuccessAlert,
  Textarea,
} from '@tmapy/style-guide';

import { QUERY_STATE } from '../../constants';
import {
  toBtnQueryState,
  useAuth,
  useFormItemServerErrors,
  useInput,
  useSelect,
  useSelector,
} from '../../hooks';
import type { InputProps } from '../../hooks/useInput';
import { docsInternalInfoPartialUpdate } from '../../openapi/operations/docsInternalInfoPartialUpdate';
import { usersList } from '../../openapi/operations/usersList';
import type { DocRetrieveResponse, UserListResponse } from '../../openapi/types';
import type { ServerErrors } from '../../types';
import { KRAJ_STAFF_ROLES } from '../constants';

import { docInternalInfoMessages } from './docsMessages';

type ResponsiblePerson = Pick<UserListResponse, 'url' | 'name' | 'surname' | 'username'>;

type DocInternalInfoProps = {
  doc: DocRetrieveResponse;
};

export const useCanManageDocInternalInfo = () => {
  const hasModule = useSelector(
    (state) => state.app.config.manager?.modules?.internalInfo ?? false,
  );
  const auth = useAuth();
  return hasModule && auth.hasPermission('Docs', 'internal_info', null);
};

const renderUser = (user: ResponsiblePerson | null) =>
  user ? `${[user.name, user.surname].join(' ')} (${user.username})` : '';

const prepareKrajStaff = async (search: string) => {
  const response = await usersList({
    query: { search, roles: KRAJ_STAFF_ROLES, is_active: true, page_size: 50 },
  });
  return response.ok ? response.data?.results : null;
};

const DocInternalInfoForm: React.FC<DocInternalInfoProps> = ({ doc }) => {
  const formatMessage = useMessage();
  const [saveQueryState, setSaveQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [saveErrors, setSaveErrors] = useState<ServerErrors | null>(null);
  const formItemServerErrors = useFormItemServerErrors(saveErrors);

  const resetSaveState = useCallback(() => {
    setSaveQueryState(QUERY_STATE.EMPTY);
    setSaveErrors(null);
  }, []);

  const responsiblePerson = useSelect<ResponsiblePerson | null, UserListResponse>({
    name: 'responsible_person',
    label: formatMessage(docInternalInfoMessages.responsiblePerson),
    initialValue: doc.responsible_person,
    onChange: resetSaveState,
    prepareItems: prepareKrajStaff,
    renderValue: renderUser,
    renderItem: renderUser,
    ...formItemServerErrors.responsible_person,
  });

  const internalNote = useInput<InputProps>({
    name: 'internal_note',
    label: formatMessage(docInternalInfoMessages.internalNote),
    initialValue: doc.internal_note,
    onChange: resetSaveState,
    ...formItemServerErrors.internal_note,
  });

  const handleSave = useCallback(async () => {
    setSaveQueryState(QUERY_STATE.WAITING);
    setSaveErrors(null);
    const response = await docsInternalInfoPartialUpdate({
      query: { id: doc.id },
      request: {
        responsible_person: responsiblePerson.value?.url ?? null,
        internal_note: internalNote.value.trim() || null,
      },
    });
    if (response.ok) {
      setSaveQueryState(QUERY_STATE.SUCCESS);
    } else {
      setSaveQueryState(QUERY_STATE.FAIL);
      setSaveErrors(response.error?.data ?? null);
    }
  }, [doc.id, responsiblePerson.value, internalNote.value]);

  const nonFieldError = formItemServerErrors.non_field_errors?.errorMessage;
  const hasFieldError = !!(
    formItemServerErrors.responsible_person || formItemServerErrors.internal_note
  );

  return (
    <section>
      <h3 className='sg-u-type-h2 sg-a-ml-2'>
        <FormattedMessage {...docInternalInfoMessages.title} />
      </h3>
      <div className='sg-u-box sg-a-p-2 sg-a-mt-1 sg-u-vs-2'>
        <FormRow>
          <FormItem {...responsiblePerson}>
            <CustomSelect {...responsiblePerson} />
          </FormItem>
        </FormRow>
        <FormRow>
          <FormItem {...internalNote}>
            <Textarea {...internalNote} />
            <div className='sg-a-mt-1/2 sg-u-type-sml sg-u-c-body-darkest'>
              <FormattedMessage {...docInternalInfoMessages.internalNoteHint} />
            </div>
          </FormItem>
        </FormRow>
        {saveQueryState === QUERY_STATE.SUCCESS && (
          <SuccessAlert>
            <FormattedMessage {...docInternalInfoMessages.saveSuccess} />
          </SuccessAlert>
        )}
        {saveQueryState === QUERY_STATE.FAIL && !hasFieldError && (
          <DangerAlert>
            {nonFieldError ?? <FormattedMessage {...docInternalInfoMessages.saveFail} />}
          </DangerAlert>
        )}
        <BtnBar>
          <SecondaryBtn
            queryState={toBtnQueryState(saveQueryState)}
            isDisabled={saveQueryState === QUERY_STATE.WAITING}
            onClick={handleSave}
          >
            <FormattedMessage {...docInternalInfoMessages.save} />
          </SecondaryBtn>
        </BtnBar>
      </div>
    </section>
  );
};

export const DocInternalInfo: React.FC<DocInternalInfoProps> = ({ doc }) =>
  useCanManageDocInternalInfo() ? <DocInternalInfoForm doc={doc} /> : null;
